import { useEffect, useState } from 'react';
import { getRiskScores } from '@/api/custom';
import { onRiskScoresChanged } from './risk-score-events';

export const useAccountRiskScores = (
  firmId?: string | null,
  userId?: string | null,
): { profileScore: number | null; scoreByAccountId: Map<string, number>; loading: boolean } => {
  const trimmedFirmId = firmId?.trim() || '';
  const trimmedUserId = userId?.trim() || '';
  const [scoreByAccountId, setScoreByAccountId] = useState<Map<string, number>>(() => new Map());
  const [profileScore, setProfileScore] = useState<number | null>(null);
  const [loading, setLoading] = useState(() => Boolean(trimmedFirmId && trimmedUserId));
  const [version, setVersion] = useState(0);

  useEffect(() => onRiskScoresChanged(() => setVersion((current) => current + 1)), []);

  useEffect(() => {
    if (!trimmedFirmId || !trimmedUserId) {
      setScoreByAccountId(new Map());
      setProfileScore(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void getRiskScores({ firm_id: trimmedFirmId, user_id: trimmedUserId })
      .then((response) => {
        if (cancelled) return;
        const records = Array.isArray(response.data)
          ? response.data
          : response.data
            ? [response.data]
            : [];
        const next = new Map<string, number>();
        let nextProfileScore: number | null = null;
        for (const record of records) {
          if (!record || !Number.isFinite(record.risk_score)) continue;
          const accountId = record.account_id?.trim();
          if (accountId) {
            next.set(accountId, record.risk_score);
          } else if (nextProfileScore == null) {
            // A record with no account is the investor's profile (default) score.
            nextProfileScore = record.risk_score;
          }
        }
        setScoreByAccountId(next);
        setProfileScore(nextProfileScore);
      })
      .catch((error: unknown) => {
        console.error('Failed to load account risk scores for wealth page', error);
        if (!cancelled) {
          setScoreByAccountId(new Map());
          setProfileScore(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [trimmedFirmId, trimmedUserId, version]);

  return { profileScore, scoreByAccountId, loading };
};
